import React, { useMemo, useState } from 'react'
import moment from 'moment'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { DataGrid } from '@mui/x-data-grid'
import BigModal from '../../components/Modals/BigModal'
import Impersonate from './Impersonate'
import SupportRequestForm from '../../pages/dev/SupportRequestForm'
import KeyValueList from '../kit/KeyValueList'
import dataGridSx from '../kit/dataGridSx'
import { formatDateTime, fromNow, lastActivityColour } from '../kit/helpers'

const fullName = (user) => [user.prefix, user.firstName, user.lastName].filter(Boolean).join(' ')

export const roleLabel = (user) =>
  user.security?.isTenantOwner ? 'Tenant Owner' : user.security?.isSiteAdmin ? 'Site Admin' : 'Standard'

export const accountStatus = (user) => {
  if (user.security?.archived) return { label: 'Archived', color: 'default', variant: 'outlined' }
  if (user.security?.canLogIn === false) return { label: 'Login disabled', color: 'anRed' }
  if (!user.hasLoggedIn) return { label: 'Never logged in', color: 'anOrange' }
  return { label: 'Active', color: 'anGreen' }
}

const linkedProfile = (user) =>
  user.employee
    ? 'Employee'
    : user.subcontractor
      ? 'Subcontractor'
      : user.ptsOrg
        ? 'PTS Organisation'
        : user.clientContact
          ? 'Client Contact'
          : user.employeeApplication
            ? 'Applicant'
            : 'None'

const yesNo = (value) => (value ? 'Yes' : 'No')

const latestPushToken = (user) =>
  (user.pushTokens ?? [])
    .slice()
    .sort((a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime())[0]

const UserDetailDialog = ({ user, onClose, onImpersonate, onSupportRequest }) => {
  if (!user) return null
  const status = accountStatus(user)
  const pushToken = latestPushToken(user)

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Box>{fullName(user)}</Box>
          <Stack direction="row" gap={1}>
            <Chip label={roleLabel(user)} color="anPurple" size="small" />
            <Chip label={status.label} color={status.color} variant={status.variant ?? 'filled'} size="small" />
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack gap={3} paddingTop={0.5}>
          <Box>
            <Typography fontWeight={700} marginBottom={1}>
              Account
            </Typography>
            <KeyValueList
              showEmpty
              rows={[
                { label: 'Email', value: user.email },
                { label: 'Role', value: roleLabel(user) },
                {
                  label: 'Last active',
                  value: user.lastActive
                    ? `${fromNow(user.lastActive)} (${formatDateTime(user.lastActive)})`
                    : 'Never',
                },
                { label: 'Has logged in', value: user.hasLoggedIn ? 'Yes' : 'Never' },
                { label: 'Set first password', value: yesNo(user.hasSetFirstPassword) },
                { label: 'Linked profile', value: linkedProfile(user) },
                { label: 'User ID', value: user._id, fullWidth: true },
              ]}
            />
          </Box>
          <Box>
            <Typography fontWeight={700} marginBottom={1}>
              Security
            </Typography>
            <KeyValueList
              showEmpty
              rows={[
                { label: 'Can log in', value: yesNo(user.security?.canLogIn) },
                { label: 'Email confirmed', value: yesNo(user.security?.emailConfirmed) },
                { label: 'MFA configured', value: yesNo(user.security?.mfa?.configured) },
                { label: 'Archived', value: yesNo(user.security?.archived) },
              ]}
            />
          </Box>
          <Box>
            <Typography fontWeight={700} marginBottom={1}>
              Mobile app & legal
            </Typography>
            <KeyValueList
              showEmpty
              rows={[
                {
                  label: 'Mobile app',
                  value: pushToken
                    ? `${pushToken.platform ?? 'Unknown platform'} · registered ${
                        pushToken.updatedAt ? fromNow(pushToken.updatedAt) : 'date unknown'
                      }`
                    : 'Not registered',
                },
                { label: 'Push devices', value: String(user.pushTokens?.length ?? 0) },
                { label: 'Terms accepted', value: formatDateTime(user.legal?.acceptedTermsAt) || 'No' },
                { label: 'Privacy accepted', value: formatDateTime(user.legal?.acceptedPrivacyAt) || 'No' },
              ]}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button color="primary" onClick={() => onSupportRequest(user)}>
          Add support request
        </Button>
        <Button color="anBlue" variant="contained" onClick={() => onImpersonate(user._id)}>
          Log in as
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Tenant user list with account detail, impersonation and support-request
// actions. The parent loads the users once and passes them down.
const UserAccounts = ({ tenantID, subdomain, users = [], loading = false }) => {
  const [search, setSearch] = useState('')
  const [detailUser, setDetailUser] = useState(null)
  const [impersonateUser, setImpersonateUser] = useState(null)
  const [supportUser, setSupportUser] = useState(null)

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return users
    return users.filter((user) =>
      [user.firstName, user.lastName, user.email, roleLabel(user)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query),
    )
  }, [users, search])

  return (
    <Stack gap={1.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
        <Typography variant="body2" color="text.secondary" alignSelf="center">
          {users.length} account{users.length === 1 ? '' : 's'} · click a row for full detail
        </Typography>
        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search users…"
          size="small"
          sx={{ minWidth: { sm: 260 }, bgcolor: '#FFFFFF' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <DataGrid
        rows={rows}
        loading={loading}
        autoHeight
        rowHeight={56}
        getRowId={(row) => row._id}
        disableRowSelectionOnClick
        onRowClick={({ row }) => setDetailUser(row)}
        initialState={{
          sorting: { sortModel: [{ field: 'lastActive', sort: 'desc' }] },
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        pageSizeOptions={[25, 50, 100]}
        sx={dataGridSx}
        columns={[
          {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            minWidth: 200,
            valueGetter: (value, row) => `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim(),
            renderCell: ({ value, row }) => (
              <Stack minWidth={0}>
                <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
                  {value || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {row.email}
                </Typography>
              </Stack>
            ),
          },
          {
            field: 'role',
            headerName: 'Role',
            flex: 0.5,
            minWidth: 120,
            valueGetter: (value, row) => roleLabel(row),
            renderCell: ({ value }) =>
              value === 'Standard' ? (
                <Typography variant="body2" color="text.secondary">
                  Standard
                </Typography>
              ) : (
                <Chip label={value} color="anPurple" size="small" sx={{ borderRadius: 1.5 }} />
              ),
          },
          {
            field: 'profile',
            headerName: 'Profile',
            flex: 0.5,
            minWidth: 120,
            valueGetter: (value, row) => linkedProfile(row),
            renderCell: ({ value }) => (
              <Typography variant="body2" color={value === 'None' ? 'text.disabled' : 'text.secondary'}>
                {value}
              </Typography>
            ),
          },
          {
            field: 'lastActive',
            headerName: 'Last active',
            flex: 0.6,
            minWidth: 130,
            valueGetter: (value) => (value ? new Date(value) : null),
            sortComparator: (a, b) => (a?.getTime() ?? 0) - (b?.getTime() ?? 0),
            renderCell: ({ value }) => (
              <Typography variant="body2" sx={{ color: lastActivityColour(value) }}>
                {value ? moment(value).fromNow() : 'Never'}
              </Typography>
            ),
          },
          {
            field: 'status',
            headerName: 'Status',
            flex: 0.55,
            minWidth: 140,
            valueGetter: (value, row) => accountStatus(row).label,
            renderCell: ({ row }) => {
              const status = accountStatus(row)
              return (
                <Chip
                  label={status.label}
                  color={status.color}
                  variant={status.variant ?? 'filled'}
                  size="small"
                  sx={{ borderRadius: 1.5 }}
                />
              )
            },
          },
          {
            field: 'actions',
            headerName: '',
            flex: 0.9,
            minWidth: 220,
            sortable: false,
            renderCell: ({ row }) => (
              <Stack direction="row" gap={1} onClick={(event) => event.stopPropagation()}>
                <Button size="small" variant="outlined" color="anBlue" onClick={() => setImpersonateUser(row._id)}>
                  Log in as
                </Button>
                <Button size="small" onClick={() => setSupportUser(row)}>
                  Support request
                </Button>
              </Stack>
            ),
          },
        ]}
      />
      <UserDetailDialog
        user={detailUser}
        onClose={() => setDetailUser(null)}
        onImpersonate={(userId) => {
          setDetailUser(null)
          setImpersonateUser(userId)
        }}
        onSupportRequest={(user) => {
          setDetailUser(null)
          setSupportUser(user)
        }}
      />
      <BigModal open={!!impersonateUser} onClose={() => setImpersonateUser(null)}>
        <Impersonate tenantId={tenantID} subdomain={subdomain} userId={impersonateUser} />
      </BigModal>
      <BigModal
        open={!!supportUser}
        onClose={() => setSupportUser(null)}
        style={{ p: 0, borderRadius: 4, overflow: 'hidden', width: '95%' }}
      >
        <SupportRequestForm
          adminMode
          tenantId={tenantID}
          subdomain={subdomain}
          userId={supportUser?._id}
          userName={supportUser?.firstName}
          userEmail={supportUser?.email}
          onClose={() => setSupportUser(null)}
        />
      </BigModal>
    </Stack>
  )
}

export default UserAccounts
